// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export type TourPhotoSize = 'thumb' | 'main';

/**
 * Builds the URL for one tour photo's bytes.
 *
 * Always routes through the BFF, never at S3 directly: the `goffy-user-images` bucket is private
 * and the BFF streams the bytes with its own credentials. Unlike {@link getTourImageUri} this route
 * is authenticated (a tour photo is a member-private snapshot of identifiable people, and is never
 * rendered before sign-in), so callers must attach the bearer token — see `AuthedImage`, which is
 * the only thing that should build these URLs for rendering.
 *
 * The `v` token is not decoration. The BFF serves these bytes with
 * `Cache-Control: immutable`, so without a version that changes when the resize pipeline swaps the
 * original for a downscaled rendition, clients would keep showing the cached original forever.
 */
export const getTourPhotoUri = (
  apiBaseUrl: string,
  tourId: string | null | undefined,
  photoId: string | null | undefined,
  size: TourPhotoSize,
  hasRendition: boolean,
): string | null => {
  const trimmedTourId = tourId?.trim();
  const trimmedPhotoId = photoId?.trim();
  if (!trimmedTourId || !trimmedPhotoId) {
    return null;
  }

  const normalizedBaseUrl = apiBaseUrl.replace(/\/+$/, '');
  // Two states only — original, or resized. That is the whole lifecycle of a photo's bytes, so a
  // one-bit token is enough to flip the URL exactly once when the resize lands.
  const version = hasRendition ? `${size}-1` : 'orig';
  return (
    `${normalizedBaseUrl}/api/tours/${encodeURIComponent(trimmedTourId)}` +
    `/photos/${encodeURIComponent(trimmedPhotoId)}/image` +
    `?size=${size}&v=${version}`
  );
};

/**
 * The shareable link for a photo a member has made public.
 *
 * Unlike {@link getTourPhotoUri} this route is anonymous — no bearer token, no `AuthedImage` —
 * because it is meant to be handed to people outside the tour. The BFF answers 404 unless the
 * photo's `isPublic` flag is on, so building this URL for a private photo yields a dead link, not
 * a leak.
 */
export const getPublicTourPhotoUrl = (
  apiBaseUrl: string,
  tourId: string | null | undefined,
  photoId: string | null | undefined,
): string | null => {
  const trimmedTourId = tourId?.trim();
  const trimmedPhotoId = photoId?.trim();
  if (!trimmedTourId || !trimmedPhotoId) {
    return null;
  }

  const normalizedBaseUrl = apiBaseUrl.replace(/\/+$/, '');
  return (
    `${normalizedBaseUrl}/api/tours/${encodeURIComponent(trimmedTourId)}` +
    `/photos/${encodeURIComponent(trimmedPhotoId)}/public?size=main`
  );
};
